import { Link } from 'react-router-dom'
import { formatDateRange } from '../../lib/dates'
import { clampProgress } from '../../lib/progress'
import type { DashboardUpcoming, EventStatus } from '../../types'

// UpcomingEvents - rows for the next ~6 events. Each row has a type colour
// stripe at the left edge, name + date range + manager + small progress bar.
// Click navigates to the event detail page.

interface Props {
  events: DashboardUpcoming[]
  loading: boolean
}

export function UpcomingEvents({ events, loading }: Props) {
  return (
    <div className="bg-white border border-line rounded">
      <div className="px-5 py-3 border-b border-line flex items-center justify-between">
        <h2 className="font-display text-lg">Upcoming events</h2>
        <Link to="/events" className="text-xs text-green font-semibold hover:underline">
          View all →
        </Link>
      </div>
      <div>
        {loading && (
          <div className="px-5 py-4 text-sm text-ink-soft font-mono">Loading…</div>
        )}
        {!loading && events.length === 0 && (
          <div className="px-5 py-6 text-sm text-ink-soft italic">
            Nothing in the calendar yet. Add an event to populate this list.
          </div>
        )}
        {!loading && events.map((e) => {
          const isArchived = e.archivedAt != null
          return (
          <Link
            key={e.id}
            to={`/events/${e.id}`}
            className={
              'flex items-center gap-4 px-5 py-3 border-t border-line first:border-t-0 hover:bg-parchment transition-colors ' +
              (isArchived ? 'opacity-60' : '')
            }
          >
            <span
              aria-hidden
              className="w-1 self-stretch rounded-full"
              style={{ background: e.type.colourBorder }}
            />
            <div className="flex-1 min-w-0">
              <div className={'font-semibold text-sm truncate ' + (isArchived ? 'line-through' : '')}>
                {e.name}
              </div>
              <div className="text-xs text-ink-soft mt-0.5 flex items-center gap-2 flex-wrap">
                <span
                  className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                  style={{ background: e.type.colourBg, color: e.type.colourText }}
                >
                  {e.type.name}
                </span>
                {isArchived && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-ink-soft/15 text-ink-soft uppercase tracking-wide">
                    Archived
                  </span>
                )}
                <span className="font-mono">{formatDateRange(e.startDate, e.endDate)}</span>
                <StatusDot status={e.status} />
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-3 flex-shrink-0">
              {e.manager ? (
                <div
                  className="w-7 h-7 rounded-full bg-green text-parchment text-xs font-semibold flex items-center justify-center"
                  title={e.manager.name ?? 'Manager'}
                >
                  {e.manager.avatarInitials ?? '?'}
                </div>
              ) : (
                <span className="text-xs italic text-ink-soft" title="Unassigned">
                  —
                </span>
              )}
              <div className="w-16">
                <div className="flex items-center justify-between text-[10px] text-ink-soft mb-1">
                  <span>Build</span>
                  <span className="font-mono">{e.progress}%</span>
                </div>
                <div className="h-1.5 bg-parchment rounded overflow-hidden">
                  <div
                    className="h-full bg-green"
                    style={{ width: `${clampProgress(e.progress)}%` }}
                  />
                </div>
              </div>
            </div>
          </Link>
          )
        })}
      </div>
    </div>
  )
}

function StatusDot({ status }: { status: EventStatus }) {
  const colour: Record<EventStatus, string> = {
    'Idea': '#9b7f2a',
    'Draft': '#9aa39a',
    'In Development': '#9b7f2a',
    'Live': '#7a2329',
    'Complete': '#0a4a3a',
    'Cancelled': '#9aa39a'
  }
  return (
    <span className="inline-flex items-center gap-1">
      <span
        aria-hidden
        className="inline-block w-1.5 h-1.5 rounded-full"
        style={{ background: colour[status] }}
      />
      <span>{status}</span>
    </span>
  )
}
